import React, { useEffect, useRef } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import { Box, useTheme } from "@mui/material";
import { toolbarOptions } from "../../../../models/TextEditor/projectDetails";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  minHeight?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "Write something...",
  maxLength = 500,
  disabled = false,
  minHeight = "154px",
}) => {
  const theme = useTheme();
  const quillRef = useRef<HTMLDivElement | null>(null);
  const editorInstance = useRef<Quill | null>(null);

  useEffect(() => {
    if (!quillRef.current) return;

    editorInstance.current = new Quill(quillRef.current, {
      theme: "snow",
      readOnly: disabled,
      modules: {
        toolbar: disabled
          ? false
          : {
              container: toolbarOptions,
              handlers: {
                undo: () => editorInstance.current?.history.undo(),
                redo: () => editorInstance.current?.history.redo(),
              },
            },
        history: {
          delay: 1000,
          maxStack: 100,
          userOnly: true,
        },
      },
      placeholder,
    });

    editorInstance.current.root.innerHTML = value;

    editorInstance.current.on("text-change", () => {
      const text = editorInstance.current!.getText().trim();
      if (text.length > maxLength) {
        editorInstance.current!.deleteText(maxLength, text.length);
      } else {
        onChange(editorInstance.current!.root.innerHTML);
      }
      // onChange(editorInstance.current!.root.innerHTML);
    });

    return () => {
      editorInstance.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (editorInstance.current) {
      const currentHTML = editorInstance.current.root.innerHTML;
      if (currentHTML !== value) {
        editorInstance.current.root.innerHTML = value;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column-reverse",
        border: `1px solid ${theme.palette.customColors.greyDarkborder}`,
        borderRadius: "4px",
        overflow: "visible",
        pointerEvents: disabled ? "none" : "auto",
        backgroundColor: disabled ? "#f9f9f9" : "inherit",
        "& .ql-container": {
          minHeight: minHeight,
          borderLeft: "none",
          borderRight: "none",
        },
        "& .ql-toolbar": {
          borderTop: "none",
          borderBottom: "none",
          boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px",
        },
        "& .ql-editor.ql-blank::before": {
          fontStyle: "normal",
          color: "gray",
          fontSize: "14px",
          opacity: 1,
        },
      }}
    >
      <div ref={quillRef} />
      <Box
        sx={{
          fontSize: "12px",
          color: "gray",
          textAlign: "right",
          padding: "4px 8px",
        }}
      >
        {editorInstance.current?.getText().trim().length || 0}/{maxLength}{" "}
        characters
      </Box>
    </Box>
  );
};

export default RichTextEditor;
